import { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { AlertCircle, CheckCircle, Loader, Lock, Eye, EyeOff, CheckCircle2 } from 'lucide-react';
import toast from 'react-hot-toast';
import adminApiService from '../../services/adminApi';

// Password strength requirements
const PASSWORD_REQUIREMENTS = {
  minLength: { label: 'At least 8 characters', test: (p: string) => p.length >= 8 },
  hasUppercase: { label: 'One uppercase letter', test: (p: string) => /[A-Z]/.test(p) },
  hasLowercase: { label: 'One lowercase letter', test: (p: string) => /[a-z]/.test(p) },
  hasNumber: { label: 'One number', test: (p: string) => /[0-9]/.test(p) },
  hasSpecial: { label: 'One special character', test: (p: string) => /[!@#$%^&*(),.?":{}|<>]/.test(p) },
};

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [formData, setFormData] = useState({
    newPassword: '',
    confirmPassword: '',
  });
  const [validationError, setValidationError] = useState('');

  // Password visibility states
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const token = searchParams.get('token');

  // Password strength calculation
  const passwordStrength = useMemo(() => {
    if (!formData.newPassword) return { strength: 0, label: '', color: '' };
    
    let strength = 0;
    for (const requirement of Object.values(PASSWORD_REQUIREMENTS)) {
      if (requirement.test(formData.newPassword)) strength++;
    }
    
    const total = Object.keys(PASSWORD_REQUIREMENTS).length;
    const percentage = (strength / total) * 100;
    
    if (percentage <= 20) return { strength: percentage, label: 'Very Weak', color: 'bg-red-500' };
    if (percentage <= 40) return { strength: percentage, label: 'Weak', color: 'bg-orange-500' };
    if (percentage <= 60) return { strength: percentage, label: 'Fair', color: 'bg-yellow-500' };
    if (percentage <= 80) return { strength: percentage, label: 'Strong', color: 'bg-blue-500' };
    return { strength: percentage, label: 'Very Strong', color: 'bg-green-500' };
  }, [formData.newPassword]);

  // Check if form is complete
  const isFormComplete = useMemo(() => {
    return formData.newPassword.trim() !== '' && formData.confirmPassword.trim() !== '';
  }, [formData]);

  useEffect(() => {
    if (!token) {
      toast.error('Invalid password reset link. Please request a new one.');
      navigate('/login');
    }
  }, [token, navigate]);

  const validatePassword = (password: string): string => {
    if (password.length < 8) {
      return 'Password must be at least 8 characters long';
    }
    if (!/[A-Z]/.test(password)) {
      return 'Password must contain at least one uppercase letter';
    }
    if (!/[a-z]/.test(password)) {
      return 'Password must contain at least one lowercase letter';
    }
    if (!/\d/.test(password)) {
      return 'Password must contain at least one number';
    }
    if (!/[!@#$%^&*(),.?":{}|<>]/.test(password)) {
      return 'Password must contain at least one special character';
    }
    return '';
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setValidationError('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setValidationError('');

    if (!formData.newPassword || !formData.confirmPassword) {
      setValidationError('All fields are required');
      return;
    }

    const passwordError = validatePassword(formData.newPassword);
    if (passwordError) {
      setValidationError(passwordError);
      return;
    }

    if (formData.newPassword !== formData.confirmPassword) {
      setValidationError('Passwords do not match');
      return;
    }

    if (!token) {
      toast.error('Invalid reset token');
      return;
    }

    setIsLoading(true);

    try {
      const response = await adminApiService.resetPassword(token, formData.newPassword);
      setIsSuccess(true);
      toast.success(response.message || 'Password has been reset successfully!');
      
      setTimeout(() => {
        navigate('/login');
      }, 2000);
    } catch (error: any) {
      console.error('Password reset error:', error);
      
      // Extract error message from different possible sources
      const errorMessage = 
        error?.message || 
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        'Failed to reset password. The link may have expired. Please request a new one.';
      
      toast.error(errorMessage);
      setValidationError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  if (isSuccess) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br bg-[#8b181c] via-background to-muted p-4">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6">
            <div className="text-center space-y-6">
              <div className="flex justify-center">
                <div className="rounded-full bg-green-100 dark:bg-green-900/20 p-3">
                  <CheckCircle className="h-12 w-12 text-green-600 dark:text-green-400" />
                </div>
              </div>
              <div className="space-y-2">
                <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                  Password Reset Successful
                </h1>
                <p className="text-gray-600 dark:text-gray-400">
                  Your password has been reset successfully. You can now login with your new password.
                </p>
              </div>
              <Button
                onClick={() => navigate('/login')}
                className="w-full bg-[#040707] hover:bg-opacity-50 hover:text-black text-white"
              >
                Go to Login
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br bg-[#8b181c] via-background to-muted p-4">
      <Card className="w-full max-w-md">
        <CardContent className="pt-6 bg-white">
          <div className="space-y-6">
            <div className="text-center space-y-2">
              <div className="flex justify-center">
                <div className="rounded-full bg-[#040707] p-3">
                  <Lock className="h-8 w-8 text-white" />
                </div>
              </div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                Reset Your Password
              </h1>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              {validationError && (
                <div className="flex items-center gap-2 p-3 text-sm text-red-800 bg-red-50 dark:bg-red-900/20 dark:text-red-300 rounded-lg">
                  <AlertCircle className="h-4 w-4" />
                  <span>{validationError}</span>
                </div>
              )}

              <div className="grid gap-2">
                <Label htmlFor="newPassword">New Password</Label>
                <div className="relative">
                  <Input
                    id="newPassword"
                    type={showNewPassword ? "text" : "password"}
                    placeholder="Enter your new password"
                    value={formData.newPassword}
                    onChange={(e) => handleInputChange('newPassword', e.target.value)}
                    required
                    disabled={isLoading}
                    className="pr-10"
                    autoComplete="new-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 focus:outline-none disabled:opacity-50"
                    tabIndex={-1}
                    aria-label={showNewPassword ? "Hide password" : "Show password"}
                    disabled={isLoading}
                  >
                    {showNewPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>

                {/* Password Strength Indicator */}
                {formData.newPassword && (
                  <div className="space-y-2 mt-2">
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-gray-600">Password Strength:</span>
                      <span className={`font-medium ${
                        passwordStrength.strength <= 40 ? 'text-red-600' :
                        passwordStrength.strength <= 60 ? 'text-yellow-600' :
                        passwordStrength.strength <= 80 ? 'text-blue-600' : 'text-green-600'
                      }`}>
                        {passwordStrength.label}
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-1.5">
                      <div
                        className={`h-1.5 rounded-full transition-all duration-300 ${passwordStrength.color}`}
                        style={{ width: `${passwordStrength.strength}%` }}
                      />
                    </div>

                    {/* Password Requirements Checklist */}
                    <div className="grid grid-cols-2 gap-1 mt-2">
                      {Object.entries(PASSWORD_REQUIREMENTS).map(([key, requirement]) => {
                        const isMet = requirement.test(formData.newPassword);
                        return (
                          <div key={key} className="flex items-center gap-1.5 text-xs">
                            <CheckCircle2 className={`h-3 w-3 ${isMet ? 'text-green-500' : 'text-gray-300'}`} />
                            <span className={isMet ? 'text-green-700' : 'text-gray-500'}>
                              {requirement.label}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {!formData.newPassword && (
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Must be at least 8 characters with uppercase, lowercase, number, and special character
                  </p>
                )}
              </div>

              <div className="grid gap-2">
                <Label htmlFor="confirmPassword">Confirm New Password</Label>
                <div className="relative">
                  <Input
                    id="confirmPassword"
                    type={showConfirmPassword ? "text" : "password"}
                    placeholder="Confirm your new password"
                    value={formData.confirmPassword}
                    onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
                    required
                    disabled={isLoading}
                    className="pr-10"
                    autoComplete="new-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 focus:outline-none disabled:opacity-50"
                    tabIndex={-1}
                    aria-label={showConfirmPassword ? "Hide password" : "Show password"}
                    disabled={isLoading}
                  >
                    {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                {/* Password Match Indicator */}
                {formData.confirmPassword && formData.newPassword && (
                  <div className="flex items-center gap-1.5 text-xs mt-1">
                    {formData.newPassword === formData.confirmPassword ? (
                      <>
                        <CheckCircle2 className="h-3 w-3 text-green-500" />
                        <span className="text-green-700">Passwords match</span>
                      </>
                    ) : (
                      <>
                        <AlertCircle className="h-3 w-3 text-red-500" />
                        <span className="text-red-700">Passwords do not match</span>
                      </>
                    )}
                  </div>
                )}
              </div>

              <Button
                type="submit"
                className="w-full bg-[#040707] hover:bg-opacity-50 hover:text-black text-white"
                disabled={isLoading || !isFormComplete}
              >
                {isLoading ? (
                  <div className="flex items-center justify-center">
                    <Loader className="w-4 h-4 mr-2 animate-spin" />
                    Resetting Password...
                  </div>
                ) : (
                  'Reset Password'
                )}
              </Button>

              <div className="text-center text-sm">
                <button
                  type="button"
                  onClick={() => navigate('/login')}
                  className="underline underline-offset-4 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
                  disabled={isLoading}
                >
                  Back to login
                </button>
              </div>
            </form>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
